import React from 'react';
import { Btn, Modal } from './index.jsx';
import { api } from '../lib/api';
import { formatInTz, DEFAULT_TIMEZONE } from '../lib/timezone';
import { palette as COLORS, alpha, type } from '../design';

// Mirrors ADMIN_CANCEL_REASON_MIN_LENGTH / _MAX_LENGTH in backend/src/lib/constants.js. The API
// refuses anything outside them; the dialog only saves admin the round trip.
export const ADMIN_CANCEL_REASON_MIN_LENGTH = 5;
export const ADMIN_CANCEL_REASON_MAX_LENGTH = 300;
export const ADMIN_CANCEL_FAILED_MESSAGE = 'We could not cancel this booking. Check your connection, then try again.';

const FONT = "'Plus Jakarta Sans', sans-serif";
const textStyle = { fontFamily: FONT, fontSize: type.body, color: COLORS.walnut, margin: 0, lineHeight: 1.5 };

// ADR 0007: a refund is due when the student paid the instructor. That is a confirmed booking, or
// a pending one the student marked payment sent. The server decides the same way for the emails.
export function refundDueFor(booking) {
  return booking.status === 'confirmed' || (booking.status === 'pending' && Boolean(booking.paymentSentAt));
}

// The message to show when a cancel fails. A refusal (400, 404, 409) carries the server's own
// words; anything else reads as a failed cancel admin can retry.
function cancelErrorMessage(err) {
  if (err?.status && err.status < 500 && err.data?.message) return err.data.message;
  return ADMIN_CANCEL_FAILED_MESSAGE;
}

// Admin cancels one booking with a required reason (AD-3). Nothing changes until admin taps
// Cancel booking; Keep booking closes the dialog and sends nothing. `onCancelled` receives the
// server's reply ({ booking, emailsDelivered }).
export function AdminCancelBookingDialog({ booking, onCancelled, onClose }) {
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const id = React.useId();
  const headingId = `${id}-heading`;
  const reasonId = `${id}-reason`;
  const hintId = `${id}-hint`;

  const trimmed = reason.trim();
  const tooShort = trimmed.length < ADMIN_CANCEL_REASON_MIN_LENGTH;
  const refundDue = refundDueFor(booking);
  const title = booking.session?.title || 'Class session';

  const handleCancel = async () => {
    if (tooShort || busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.post(`/admin/bookings/${booking.id}/cancel`, { reason: trimmed });
      await onCancelled?.(result);
    } catch (err) {
      setError(cancelErrorMessage(err));
      setBusy(false);
    }
  };

  const footer = (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
      <Btn size="sm" variant="subtle" onClick={() => onClose?.()} disabled={busy}>Keep booking</Btn>
      <Btn size="sm" onClick={handleCancel} disabled={tooShort || busy}>{busy ? 'Cancelling…' : 'Cancel booking'}</Btn>
    </div>
  );

  return (
    <Modal onClose={busy ? undefined : onClose} maxWidth={480} footer={footer} labelledBy={headingId}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '28px 28px 20px' }}>
        <h2 id={headingId} style={{ fontFamily: FONT, fontSize: type.h3, fontWeight: 700, color: COLORS.espresso, margin: 0, paddingRight: 40 }}>Cancel this booking?</h2>
        <p style={textStyle}>
          <strong style={{ color: COLORS.espresso }}>{booking.studentName}</strong> with {booking.instructorName}: "{title}" on {formatInTz(booking.scheduledAt, DEFAULT_TIMEZONE)}. The booking is {booking.status}.
        </p>
        <p style={textStyle}>The seat opens again. The student and the instructor each get an email with your reason.</p>
        <p style={{ ...textStyle, background: alpha('caramelGold', 'selection'), borderRadius: 10, padding: '10px 12px', color: COLORS.espresso }}>
          {refundDue
            ? 'The student already paid, so both emails say the instructor refunds the student directly.'
            : 'The student has not sent a payment, so the emails say nothing about a refund.'}
        </p>
        <label htmlFor={reasonId} style={{ fontFamily: FONT, fontSize: type.body, fontWeight: 600, color: COLORS.espresso }}>Reason for the student and the instructor</label>
        <textarea
          id={reasonId}
          value={reason}
          maxLength={ADMIN_CANCEL_REASON_MAX_LENGTH}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          aria-describedby={hintId}
          placeholder="For example: Instructor unavailable."
          style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid ' + COLORS.border, fontFamily: FONT, fontSize: type.body, color: COLORS.espresso, resize: 'vertical', outline: 'none', boxSizing: 'border-box', background: COLORS.white }}
        />
        <div id={hintId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: FONT, fontSize: type.label, color: COLORS.driftwood }}>
          <span>{trimmed.length > 0 && tooShort ? 'Write at least 5 characters.' : 'Required, 5 to 300 characters.'}</span>
          <span>{reason.length} / {ADMIN_CANCEL_REASON_MAX_LENGTH}</span>
        </div>
        {error && (
          <p role="alert" style={{ ...textStyle, fontSize: type.body, color: COLORS.errorRed }}>{error}</p>
        )}
      </div>
    </Modal>
  );
}
